import React from 'react';
import { BusinessTransportResult } from 'services/businessBilling/contract';
import { ParsedBusinessResult, UNKNOWN_NETWORK_RESULT, parseBusinessTransportResult } from 'services/businessBilling/parseResponse';
import { useDebouncedCui } from './useDebouncedCui';
import { useRequestGuard } from './useRequestGuard';

export type LookupPhase = 'idle' | 'waiting' | 'invalid' | 'loading' | 'done';

export interface BusinessLookupState {
    phase: LookupPhase;
    loading: boolean; // True from the moment the lookup is sent until its answer arrives.
    invalidCode?: 'CUI_INVALID_FORMAT';
    cui?: string; // The CUI this state is about (`loading` and `done` only).
    result?: ParsedBusinessResult; // The parsed answer (`done` only).
    // Asks again for the same CUI. Only acts when the phase is `done`, so an answer in flight is never doubled.
    // Whether a retry is allowed at all (limits, `retryAfterSeconds`) is decided by the caller.
    retry: () => void;
}

interface Settled {
    cui: string;
    attempt: number;
    result: ParsedBusinessResult;
}

/**
 * Sends the lookup once the debounced CUI is ready and reports `loading` until
 * the answer arrives. It never calls `lookup` for empty, still-typed or invalid
 * input. `scopeKey` identifies the session and journey: when it changes, every
 * answer in flight is ignored and the wait starts again. An answer is shown only
 * if it belongs to the current CUI and attempt, and only the latest request may
 * deliver it (generation counter). A scope change is also a new wait (debounce).
 */
export const useBusinessLookup = (
    rawCui: string,
    lookup: (cui: string) => Promise<BusinessTransportResult>,
    scopeKey: string = '',
): BusinessLookupState => {
    const debounced = useDebouncedCui(rawCui, undefined, scopeKey);
    const readyCui = debounced.status === 'ready' ? debounced.cui : null;
    const guard = useRequestGuard(scopeKey);

    const [settled, setSettled] = React.useState<Settled | null>(null);
    const [attempt, setAttempt] = React.useState(0);
    const lookupRef = React.useRef(lookup);
    lookupRef.current = lookup;

    React.useEffect(() => {
        if (readyCui === null) {
            setSettled(null);
            return undefined;
        }
        const isCurrent = guard.begin();
        lookupRef
            .current(readyCui)
            .then(
                (response) => parseBusinessTransportResult(response, 'lookup'),
                () => UNKNOWN_NETWORK_RESULT,
            )
            .then((result) => {
                if (isCurrent()) {
                    setSettled({ cui: readyCui, attempt, result });
                }
            });
        return () => guard.invalidate();
    }, [readyCui, scopeKey, attempt, guard]);

    const isDone =
        debounced.status === 'ready' &&
        settled !== null &&
        settled.cui === debounced.cui &&
        settled.attempt === attempt;

    const doneRef = React.useRef(false);
    doneRef.current = isDone;
    const retry = React.useCallback(() => {
        if (doneRef.current) {
            setAttempt((current) => current + 1);
        }
    }, []);

    switch (debounced.status) {
        case 'idle':
            return { phase: 'idle', loading: false, retry };
        case 'waiting':
            return { phase: 'waiting', loading: false, retry };
        case 'invalid':
            return { phase: 'invalid', loading: false, invalidCode: debounced.code, retry };
        default:
            return isDone && settled
                ? { phase: 'done', loading: false, cui: debounced.cui, result: settled.result, retry }
                : { phase: 'loading', loading: true, cui: debounced.cui, retry };
    }
};
